<script lang="ts">
  import {STEP} from '@/types/common';
  import {getContext, onDestroy, onMount} from 'svelte';
  import {getInstance} from '@/services/animation';
  import {project, step, download, preview, downloadDialog, isMobile} from '@/store';
  import {ProjectService, RenderService} from '@/services';
  import {Container, VideoControls} from '@components';
  import PreviewArea from './PreviewArea.svelte';
  import previewAreaLoadingStore from './loading-store';
  import editorStore from '../../lib/editor/editor.store';
  import EditorStyleView from '../../lib/editor/StyleView/EditorStyleView.svelte';
  import {fly} from 'svelte/transition';
  import NewPreviewSidebar from '../../components/new-preview-sidebar/NewPreviewSidebar.svelte';
  import ExtendedOptionMenu from '../../components/new-preview-sidebar/ExtendedOptionMenu.svelte';
  import {wait} from '../../utils/common';
  import {topnav} from '@/utils/top-nav-next-custom-events';
  import DownloadDialog from './DownloadDialog.svelte';
  import {logError} from '../../utils/logs';
  import {
    animationPlayerInstance,
    seekAnimationController,
    setAnimationControllerLoading
  } from '../../components/animations/AnimationController.svelte';
  import RenderManager from './RenderManager.svelte';
  import Toolbar from '../../lib/editor/Toolbar/Toolbar.svelte';
  import PopupBox from '../../components/new-preview-sidebar/PopupBox/PopupBox.svelte';
  import {cleanupHTMLTemplate} from './template-cleanup';
  import FaceEncodingSelector from '../../components/face-tracker/v2/FaceEncodingSelector.svelte';
  import Swal from 'sweetalert2';
  import statisticsService, {Statistic, StatisticsType} from '../../statistics';

  let progressBar: any;
  let zoomPercent = 1;
  let currentProgress: number = 0;
  const animationService = getInstance();
  const simpleModal: {open: Function; close: Function} = getContext('simple-modal');

  let render = false;

  const onSeekTimeline = (event: CustomEvent) => {
    try {
      animationPlayerInstance.pause();
      const progress = event.detail.value / 100;
      //console.log('seek progress', progress);
      if (progress > 0.99) return;
      seekAnimationController(event.detail.value / 100);
    } catch (err) {
      logError('Could not proceed with onChange event', err);
    }
  };

  // const playVideo = () => animationService.play();
  // const pauseVideo = () => animationService.pause();

  const playVideo = () => animationPlayerInstance.play();
  const pauseVideo = () => animationPlayerInstance.pause();
  const stopVideo = () => animationPlayerInstance.stop();
  const updateNextStep = () => step.update(STEP.RENDER);
  const updatePrevStep = () => {
    editorStore.removePopoverComponent('Quick Edit');
    step.update(STEP.SELECT_SUBTITLES);
  };

  const cleanupHighlights = () => {
    const highlightedElements = document.querySelectorAll('.highlighted-element');
    highlightedElements.forEach(element => {
      try {
        element.classList.remove('highlighted-element');
      } catch (err) {
        console.warn('Could not remove classlist', err);
        console.trace();
      }
    });
  };

  let isDownloadDialogOpen = false;
  let title = '';
  let progress = 0;

  $: title = $project?.video?.title || 'Untitled Project';

  let downloadStarted = false;
  const projectService = new ProjectService();
  const renderService = new RenderService();

  const saveProject = async (e: Event) => {
    if (downloadStarted) {
      downloadDialog.set(true);
      return;
    }

    downloadStarted = true;

    downloadDialog.set(true);
    download.reset();
    stopVideo();
    await wait(500);
    cleanupHighlights();

    if (!$project.video) throw 'Video undefined.';
    if (!$project.template) throw 'Template undefined.';

    const cleanedUpHTML = cleanupHTMLTemplate(animationService.getDom().parentElement);
    //console.log('Cleaned up html');
    let projectData;
    try {
      projectData = await projectService.create({
        video: $project.video.id,
        metadata: animationService.metadata,
        sentences: $project.selectedSentences,
        template: cleanedUpHTML,
        downloaded: true
      });
    } catch (err) {
      logError('Could not save project', err);
      downloadDialog.set(false);
      Swal.fire({
        title: 'Error',
        text: 'Could not save project. If this problem persists, please contact support.',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    const captureStatistic = await statisticsService.create(StatisticsType.VIDEO_DOWNLOAD_QUEUE_TIME);
    let renderingStats: Statistic;
    renderService.setup(animationService, projectData, (e as any).detail.qualityPreset).output($preview);
    try {
      statisticsService.save(captureStatistic, {
        type: 'start',
        timestamp: new Date().toISOString(),
        video_id: $project.video.id,
        project_id: projectData.id,
        video_data: renderService.output($preview)
      });
    } catch (err) {
      console.error('Error saving statistic', err);
    }
    renderService.attachEventListeners({
      onBegin: notification => {
        if (!notification) return;
        download.updateProgress(0);

        console.log('Starting...', notification);
        try {
          statisticsService.save(captureStatistic, {type: 'end', timestamp: new Date().toISOString()});
          (async () => {
            renderingStats = await statisticsService.create(StatisticsType.VIDEO_DOWNLOAD_RENDER_TIME);
            statisticsService.save(renderingStats, {type: 'start', timestamp: new Date().toISOString()});
          })();
        } catch (err) {
          console.error('Error saving statistic', err);
        }
      },
      onProgress: notification => {
        download.setCaptureState('begin');
        const totalFrames = animationService.duration * 114;
        const currentFrame = notification.data.frame;

        if (currentFrame) {
          let progressRaw = (currentFrame / totalFrames) * 100;
          if (progressRaw > 99) progressRaw = 99;

          const currentProgress = parseInt(progressRaw.toFixed(0));
          progress = isNaN(currentProgress) ? 0 : currentProgress;
          download.updateProgress(progress);
        }
      },
      onComplete: notification => {
        console.log('onComplete', notification);
        download.complete(renderService.currentRequestId, notification.data.downloadData.url);
        resetDownload();
        if (renderingStats) {
          try {
            statisticsService.save(renderingStats, {type: 'end', timestamp: new Date().toISOString()});
          } catch (err) {
            console.error('Error saving statistic', err);
          }
        }
      },
      onCapturEnd: () => {
        download.setCaptureState('complete');
      },
      onCompile: () => {
        download.setCompileState('begin');
      },
      onError: err => {
        logError('There was an error during processing the download', err);
        download.setCompileState('error-user-processing');
        // previewAreaLoadingStore.set(false);
        downloadDialog.set(false);
      },
      onDisconnected: err => {
        console.warn('[Encoder Crashed]', err);
        download.setCaptureState('retrying');
        // previewAreaLoadingStore.set(false);
        downloadDialog.set(false);
        resetDownload();
      }
    });
    try {
      console.log('Sending to encoder', $preview);
      renderService.sendToEncoder($project.template, $project.video, $preview);
    } catch (err) {
      download.setCaptureState('error-user-processing');
      logError('User is already processing something', err);
    }
  };

  const downloadDialogIsOpenSubscription = downloadDialog.subscribe(value => {
    isDownloadDialogOpen = value;
  });

  function resetDownload() {
    downloadStarted = false;
  }

  onMount(() => {
    window.addEventListener('reset-download', resetDownload);
    topnav.next(updateNextStep);
    topnav.previous(updatePrevStep);
    topnav.download(saveProject);
    editorStore.removePopoverComponent();
    setAnimationControllerLoading(true);
    previewAreaLoadingStore.set(true);
    animationService.onUpdate(progress => {
      if (progressBar) {
        let bar = progressBar.getProgressElement();
        bar.value = progress;
        progressBar.changeRunnerColor(bar);
      }
      currentProgress = progress;
    });
  });

  onDestroy(() => {
    // clearInterval(autoSaveIntervalTimeoutId)
    window.removeEventListener('reset-download', resetDownload);
    downloadDialogIsOpenSubscription();
    animationService.clearUpdate();
    stopVideo();
    download.reset();
  });
</script>

{#if $editorStore.active}
  <div id="preview"></div>
{/if}

<Container className="flex overflow-none">
  {#if !$isMobile}
    {#if $editorStore.stylebarActive}
      <div class="relative flex w-3/12 flex-col gap-6 bg-slate-400 bg-opacity-10">
        <EditorStyleView />
      </div>
    {:else}
      <NewPreviewSidebar />
    {/if}
  {/if}
  <RenderManager />
  <ExtendedOptionMenu />
  <div class="relative h-full flex-grow overflow-hidden" id="mainArea">
    {#if $isMobile}
      <div class="w-full bg-gradient-to-r from-primary to-secondary py-2 text-center text-xs text-white">
        To edit, kindly open your project from your desktop.
      </div>
      <div class="absolute left-0 h-full w-full opacity-0" style="z-index:50;"></div>
    {/if}
    {#if !$isMobile}
      <PopupBox />
      <Toolbar />
    {/if}
    <PreviewArea {zoomPercent} />

    <div
      in:fly={{y: 100, duration: 300}}
      out:fly={{y: 100, duration: 300}}
      class="w-full flex-shrink-0"
      style="z-index: 98;"
    >
      <!-- TODO: for future reference on zoom implementation -->
      <!-- <VideoTools on:zoom-template={zoomTemplate} /> -->
      <VideoControls
        bind:progressBar
        showDuration={true}
        on:play-video={playVideo}
        on:stop-video={stopVideo}
        on:pause-video={pauseVideo}
        on:seek-timeline={onSeekTimeline}
        progress={currentProgress}
        duration={$preview.totalDuration}
      />
    </div>
  </div>
</Container>
<FaceEncodingSelector />

<DownloadDialog bind:isDownloadDialogOpen bind:progress></DownloadDialog>

<style lang="postcss" global>
  #mainArea {
    display: flex;
    flex-direction: column;
    grid-template-rows: 55px auto 68px;
    /* grid-template-columns: repeat(0, 1, minmax(0, 1fr));
        grid-template-rows: calc(100% - 68px); */
    /* @apply bg-gray-200; */
  }
  .container-div {
    @apply flex flex-grow flex-col items-center justify-center;
  }

  .highlighted-element {
    box-shadow: 0 0 0 2px #1c73f7 !important;
  }
</style>
